import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
} from 'react';
import {
	sendSubmissionQuery,
	useAxiosInstance,
	usePoMutation,
} from '@gov-nx/api';
import { useExtraConfigContext } from '@gov-nx/core/app';
import { useMessageEvents } from '@gov-nx/core/events';
import type { WizardFormStep } from '@gov-nx/core/hooks';
import {
	useDataBoxes,
	usePersonCommunicationData,
	useProcessControl,
	useServiceAuthorization,
	useWizard,
	useFileUpload,
	DefaultUploadParameters,
} from '@gov-nx/core/hooks';
import {
	getRequiredDataBoxes,
	useCountry,
	useLocale,
} from '@gov-nx/core/service';
import { getKeys } from '@gov-nx/core/types';
import type { ServiceCode } from '../definitions/codes';
import {
	FormInstanceStep1,
	FormInstanceStep2,
	FormInstanceStep3,
	FormInstanceStep4,
	FormInstanceStep5,
	FormInstanceStep6,
	prepareSubmitData,
} from './FormDefinitions';
import type {
	ServiceContextTypes,
	TrestniOznameniFormData,
	TrestniOznameniFormDataStep1,
} from './service.types';
import { TrestniOznameniGroupTarget } from './service.types';
import { useAutorizaceContext } from 'libs/component/web/src/Autorizace/Context';

export const ServiceContext = createContext<ServiceContextTypes | null>(null);

export interface ServiceContextProviderProps {
	children: React.ReactNode;
	code: ServiceCode;
}

const AUTHORIZATION_SERVICE_NAME = 'PODANI_HATECRIME2';

export function TrestniOznameniContextProvider({
	children,
	code,
}: ServiceContextProviderProps) {
	const { t } = useLocale(code);
	const { toastMessageSuccess } = useMessageEvents();
	const {
		extraConfig: { application },
	} = useExtraConfigContext();
	const { requiredDataBoxes, isDataBoxConnected } = useDataBoxes(
		getRequiredDataBoxes(code, application)
	);
	const { axiosInstance } = useAxiosInstance();
	const { controls, setControls } = useProcessControl();
	const { allCountriesForSelect } = useCountry();
	const authContext = useAutorizaceContext();

	const personCommunicationData = usePersonCommunicationData();

	const { isLowAuthorization, isAuthorizationEnforced } =
		useServiceAuthorization(AUTHORIZATION_SERVICE_NAME);

	const submitMutation = usePoMutation({
		mutationFn: async (data: TrestniOznameniFormData) => {
			const prepared = prepareSubmitData(data);
			return sendSubmissionQuery(axiosInstance)(prepared);
		},
		onError: (error) => {
			setControls({ processError: error, processLoading: false });
		},
		onSuccess: async (data) => {
			setControls({ processLoading: false });

			const nonHighAuthorizationFlow = () => {
				toastMessageSuccess(t('formular.zprava.odeslano'));
			};

			if (authContext) {
				authContext?.onInitAuthorization(
					AUTHORIZATION_SERVICE_NAME,
					data.headers['x-autorizaceid'],
					nonHighAuthorizationFlow
				);
			} else {
				nonHighAuthorizationFlow();
			}
		},
		onSettled: () => {
			wizard.resetForms();
		},
	});

	const onSubmit = useCallback(
		async (values: TrestniOznameniFormData) => {
			setControls({ processError: null, processLoading: true });
			submitMutation.mutate(values);
		},
		[setControls, submitMutation]
	);

	const step2 = FormInstanceStep2({ code });

	const wizard = useWizard({
		steps: [
			FormInstanceStep1({ code }),
			step2,
			FormInstanceStep3({ code }),
			FormInstanceStep4({ code }),
			FormInstanceStep5({ code, personCommunicationData }),
			FormInstanceStep6({
				dataBoxTypes: requiredDataBoxes,
				isLowAuthorization,
			}),
		],
		onSubmit,
	});

	const groupTargetKeys = getKeys(TrestniOznameniGroupTarget);

	const step1 = wizard.step(
		0
	) as unknown as WizardFormStep<TrestniOznameniFormDataStep1>;
	useEffect(() => {
		const subscription = step1.formDefinition.formMethods.watch(
			(value, { name }) => {
				// reset "Popis" field when checkbox field above is unchecked
				if (
					name &&
					groupTargetKeys.includes(
						name as keyof typeof TrestniOznameniGroupTarget
					) &&
					value[name] === false
				) {
					step1.formDefinition.formMethods.resetField(
						`${name as keyof typeof TrestniOznameniGroupTarget}Popis`
					);
				}
			}
		);

		return () => subscription.unsubscribe();
	}, [groupTargetKeys, step1.formDefinition.formMethods]);

	const printscreenFileUploadHook = useFileUpload(
		step2.formMethods,
		'printscreen',
		{
			...DefaultUploadParameters,
			extensions: ['.jpg', '.jpeg', '.png', '.tif', '.tiff', '.gif'],
			maxAttachments: 20,
			multiple: true,
		}
	);

	const attachmentsFileUploadHook = useFileUpload(
		step2.formMethods,
		'priloha',
		{
			...DefaultUploadParameters,
			extensions: ['.pdf', '.mp4'],
			maxAttachments: 20,
			multiple: true,
		}
	);

	return (
		<ServiceContext.Provider
			value={{
				code,
				wizard,
				controls,
				setControls,
				requiredDataBoxes,
				isDataBoxConnected,
				personCommunicationData,
				groupTargetKeys,
				allCountriesForSelect,
				printscreenFileUploadHook,
				attachmentsFileUploadHook,
				isLowAuthorization,
				isAuthorizationEnforced,
			}}>
			{children}
		</ServiceContext.Provider>
	);
}

export const useTrestniOznameniContext = () =>
	useContext(ServiceContext) as ServiceContextTypes;
